import Repository from './Repository';

import { vue } from '../main';

const baseUrl = process.env.VUE_APP_API_URL;
const base = `${baseUrl}/channel`;

export default {
  /**
   * ===============================================================
   *                      MEMBER MANAGEMENT
   * ===============================================================
   */

  /**
   * Fetches a list of Channel Default Members and pushes them to the store.
   *
   * @returns {Promise<T | never>}
   */
  refetchMemberList() {
    return this.list().then((response) => {
      vue.$store.commit('setChannelMembers', response.data.list);
    });
  },
  /**
   * Fetches a list of Channel Default Members
   *
   * @returns {Promise<T | never>}
   */
  list() {
    return Repository.get(`${base}/member/list`);
  },
  /**
   * Adds a new member to the default channel members.
   *
   * @param groupId
   * @param name
   * @param description
   * @returns {Promise<AxiosResponse<T>>}
   */
  addMemberToGroup(groupId, name, description) {
    return Repository.post(`${base}/member/add`, {
      gid: groupId,
      name,
      description,
    });
  },
  /**
   * Deletes a member from a the default channel members.
   *
   * @param groupId
   * @param memberId
   * @returns {Promise<AxiosResponse<T>>}
   */
  deleteMemberFromGroup(groupId, memberId) {
    return Repository.post(`${base}/member/delete`, {
      gid: groupId,
      id: memberId,
    });
  },
  /**
   * Modifies the given members information (name, description)
   *
   * @param groupId
   * @param memberId
   * @param name
   * @param description
   * @returns {Promise<AxiosResponse<T>>}
   */
  editMemberInGroup(groupId, memberId, name, description = null) {
    return Repository.post(`${base}/member/modify`, {
      gid: groupId,
      id: memberId,
      name,
      description,
    });
  },
  /**
   * Moves the given member to another group or inside the very same group.
   *
   * @param gid
   * @param target
   * @param id
   * @param position
   * @returns {Promise<AxiosResponse<T>>}
   */
  moveMember(gid, target, id, position) {
    return Repository.post(`${base}/member/move`, {
      gid,
      target,
      id,
      position,
    });
  },

  /**
   * ===============================================================
   *                        GROUP MANAGEMENT
   * ===============================================================
   */
  /**
   * Modifies the groups information.
   *
   * @param groupId
   * @param title
   * @param color
   * @returns {Promise<AxiosResponse<T>>}
   */
  modifyGroup(groupId, title = null, color = null) {
    return Repository.post(`${base}/group/modify`, {
      id: groupId,
      title,
      color,
    });
  },
  /**
   * Deletes the given group from the channel.
   *
   * @param groupId
   * @returns {Promise<AxiosResponse<T>>}
   */
  deleteGroup(groupId) {
    return Repository.post(`${base}/group/delete`, { id: groupId });
  },
  /**
   * Adds a new group to the channel.
   *
   * @param title
   * @param column
   * @param color
   * @returns {Promise<AxiosResponse<T>>}
   */
  addGroup(title, column, color) {
    return Repository.post(`${base}/group/add`, {
      title,
      column,
      color,
    });
  },
  /**
   * Moves the groups position.
   *
   * @param groupId
   * @param position
   * @param column
   * @returns {Promise<AxiosResponse<T>>}
   */
  moveGroup(groupId, position, column = 0) {
    return Repository.post(`${base}/group/move`, {
      id: groupId,
      position,
      column,
    });
  },
  /**
   * Moves the group upwards.
   *
   * @param group
   * @returns {*|Promise<AxiosResponse<T>>}
   */
  moveGroupUp(group) {
    return this.moveGroup(group.id, group.order - 1, group.column);
  },
  /**
   * Moves the group downwards.
   *
   * @param group
   * @returns {*|Promise<AxiosResponse<T>>}
   */
  moveGroupDown(group) {
    return this.moveGroup(group.id, group.order + 1, group.column);
  },
  /**
   * Moves the groups position to the left.
   *
   * @param group
   * @returns {*|Promise<AxiosResponse<T>>}
   */
  moveGroupLeft(group) {
    return this.moveGroup(group.id, group.order, 0);
  },
  /**
   * Moves the groups position to the right.
   *
   * @param group
   * @returns {*|Promise<AxiosResponse<T>>}
   */
  moveGroupRight(group) {
    return this.moveGroup(group.id, group.order, 1);
  },
};
